import { FormEvent, useEffect, useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Sheet, toast } from '@elbklar/ui';

import { SelectField, TextField, TextareaField } from '@/components/Felder';
import { radarApi } from '@/lib/api';
import { hatFehler, hoechstens, httpsAdresse, ohne, pflicht, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import type { RadarActor, RadarActorType } from '@/types';
import { ACTOR_TYPE_TEXT, optionen } from './radarText';

interface Props {
  offen: boolean;
  onSchliessen: () => void;
  onGespeichert: () => void;
  /** Gesetzt = bearbeiten, leer = neuer Akteur. */
  akteur?: RadarActor | null;
}

const LEER = {
  name: '',
  type: 'netzwerk' as RadarActorType,
  region: '',
  url: '',
  programs_url: '',
  note: '',
};

type Feld = 'name' | 'region' | 'url' | 'programs_url';

function ausAkteur(akteur: RadarActor) {
  return {
    name: akteur.name,
    type: akteur.type,
    region: akteur.region ?? '',
    url: akteur.url ?? '',
    programs_url: akteur.programs_url ?? '',
    note: akteur.note ?? '',
  };
}

/**
 * Akteure sind die Stellen, die Programme ausschreiben — Förderbank, Kammer,
 * Netzwerk. Wer hier steht, wird bei der Suche abgeklappert.
 */
export function ActorDialog({ offen, onSchliessen, onGespeichert, akteur }: Props) {
  const formId = useId();
  const [formular, setFormular] = useState(LEER);
  const [fehler, setFehler] = useState<Fehler<Feld>>({});
  const bearbeiten = Boolean(akteur);

  useEffect(() => {
    if (!offen) return;
    setFormular(akteur ? ausAkteur(akteur) : LEER);
    setFehler({});
  }, [offen, akteur]);

  /** Wert setzen und den Fehler des Felds zurücknehmen. */
  const setze = (feld: Feld, wert: string) => {
    setFormular({ ...formular, [feld]: wert });
    setFehler((f) => ohne(f, feld));
  };

  const speichern = useMutation({
    mutationFn: () => {
      const nutzdaten = {
        name: formular.name.trim(),
        type: formular.type,
        region: formular.region.trim() || null,
        url: formular.url.trim() || null,
        programs_url: formular.programs_url.trim() || null,
        note: formular.note.trim() || null,
      };
      return akteur
        ? radarApi.updateActor(akteur.id, nutzdaten)
        : radarApi.createActor(nutzdaten);
    },
    onSuccess: () => {
      toast.success(bearbeiten ? 'Akteur gespeichert' : 'Akteur angelegt');
      onGespeichert();
      onSchliessen();
    },
    // Bei einer 422 ist `detail` eine Liste von Objekten; als Kind eines
    // Toasts brächte React die Seite zur Fehlergrenze. Nur Text anzeigen.
    onError: (fehlschlag: { response?: { status?: number; data?: { detail?: unknown } } }) =>
      toast.error(
        fehlschlag.response?.status === 409
          ? 'Einen Akteur mit diesem Namen gibt es schon'
          : typeof fehlschlag.response?.data?.detail === 'string'
            ? fehlschlag.response.data.detail
            : 'Speichern fehlgeschlagen'
      ),
  });

  // Grenzen aus RadarActorCreate (finance-api); die Adressen zusätzlich mit
  // https://, sonst findet die Suche die Seiten nicht.
  const absenden = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const neu: Fehler<Feld> = {
      name: pflicht(formular.name, 'Name fehlt') ?? hoechstens(formular.name, 255),
      region: hoechstens(formular.region, 120),
      url: httpsAdresse(formular.url, { max: 500 }),
      programs_url: httpsAdresse(formular.programs_url, { max: 500 }),
    };
    setFehler(neu);
    if (hatFehler(neu)) {
      zumErstenFehler(event.currentTarget);
      return;
    }
    speichern.mutate();
  };

  return (
    <Sheet
      open={offen}
      onOpenChange={(o) => !o && onSchliessen()}
      title={bearbeiten ? 'Akteur bearbeiten' : 'Akteur anlegen'}
      footer={
        <>
          <Button variant="ghost" onClick={onSchliessen} disabled={speichern.isPending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={speichern.isPending}>
            {bearbeiten ? 'Akteur speichern' : 'Akteur anlegen'}
          </Button>
        </>
      }
    >
      <form noValidate id={formId} onSubmit={absenden} className="space-y-4">
        <TextField
          label="Name"
          placeholder="z. B. SAB Sachsen"
          value={formular.name}
          onChange={(e) => setze('name', e.target.value)}
          error={fehler.name}
          required
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Typ"
            options={optionen(ACTOR_TYPE_TEXT)}
            value={formular.type}
            onChange={(e) =>
              setFormular({ ...formular, type: e.target.value as RadarActorType })
            }
          />
          <TextField
            label="Region"
            placeholder="z. B. Sachsen"
            value={formular.region}
            onChange={(e) => setze('region', e.target.value)}
            error={fehler.region}
          />
        </div>

        <TextField
          label="Website"
          type="url"
          placeholder="https://…"
          value={formular.url}
          onChange={(e) => setze('url', e.target.value)}
          error={fehler.url}
        />

        <TextField
          label="Programmübersicht (Link)"
          type="url"
          placeholder="https://…"
          hint="Die Seite, auf der die Programme dieses Akteurs stehen"
          value={formular.programs_url}
          onChange={(e) => setze('programs_url', e.target.value)}
          error={fehler.programs_url}
        />

        <TextareaField
          label="Notiz"
          rows={2}
          value={formular.note}
          onChange={(e) => setFormular({ ...formular, note: e.target.value })}
        />

      </form>
    </Sheet>
  );
}
